"use client";

import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { captureAnalyticsEvent } from "@/utils/analytics.utils";
import { Button } from "./Button";
import { TextArea } from "./inputs";
import { Popover } from "./Popover";
import { Tag } from "./Tag";
import { WilsonFace } from "./WilsonFace";
type SendState = "idle" | "sending" | "sent" | "failed";
type FeedbackPopoverCopy = {
  link: string;
  title: string;
  placeholder: string;
  footnote: string;
  send: string;
  sent: string;
  failed: string;
};
type FeedbackPromptCopy = FeedbackPopoverCopy & {
  tag: string;
  message?: string;
};
export function useFeedbackSend({
  onSend,
  surface
}: {
  onSend: (note: string) => Promise<void>;
  surface: string;
}) {
  const [note, setNote] = useState("");
  const [sendState, setSendState] = useState<SendState>("idle");
  const isSending = sendState === "sending";
  const send = async () => {
    if (!note.trim() || isSending) return;
    setSendState("sending");
    try {
      await onSend(note.trim());
      captureAnalyticsEvent("surface_feedback_sent", {
        surface
      });
      setSendState("sent");
    } catch {
      setSendState("failed");
    }
  };
  return {
    note,
    setNote,
    sendState,
    setSendState,
    isSending,
    send
  };
}
export function FeedbackPrompt({
  copy,
  onSend,
  surface,
  maxLength,
  className
}: {
  copy: FeedbackPromptCopy;
  onSend: (note: string) => Promise<void>;
  surface: string;
  maxLength?: number;
  className?: string;
}) {
  return <span className={cn("pointer-events-auto flex items-center gap-2.5 text-footnote text-ink-2", className)} data-sentry-component="FeedbackPrompt" data-sentry-source-file="FeedbackPrompt.tsx">
            <Tag tone="neutral" data-sentry-element="Tag" data-sentry-source-file="FeedbackPrompt.tsx">{copy.tag}</Tag>
            {copy.message && <span>{copy.message}</span>}
            <FeedbackPopover copy={copy} onSend={onSend} surface={surface} maxLength={maxLength} trigger={<span>{copy.link} →</span>} triggerClassName="text-cobalt-deep hover:underline" data-sentry-element="FeedbackPopover" data-sentry-source-file="FeedbackPrompt.tsx" />
        </span>;
}
export function FeedbackPopover({
  copy,
  onSend,
  surface,
  maxLength,
  trigger,
  triggerClassName,
  panelClassName,
  align,
  onOpenChange
}: {
  copy: FeedbackPopoverCopy;
  onSend: (note: string) => Promise<void>;
  surface: string;
  maxLength?: number;
  trigger: ReactNode;
  triggerClassName?: string;
  panelClassName?: string;
  align?: "start" | "end";
  onOpenChange?: (isOpen: boolean) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    note,
    setNote,
    sendState,
    setSendState,
    send
  } = useFeedbackSend({
    onSend,
    surface
  });
  const isSending = sendState === "sending";
  const noteRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (isOpen) noteRef.current?.focus();
  }, [isOpen]);
  const handleOpenChange = (isNextOpen: boolean) => {
    setIsOpen(isNextOpen);
    onOpenChange?.(isNextOpen);
    if (!isNextOpen && sendState === "sent") {
      setNote("");
      setSendState("idle");
    }
  };
  return <Popover label={copy.link} open={isOpen} onOpenChange={handleOpenChange} align={align} triggerClassName={triggerClassName} panelClassName={cn("w-[360px] p-4", panelClassName)} trigger={trigger} data-sentry-element="Popover" data-sentry-component="FeedbackPopover" data-sentry-source-file="FeedbackPrompt.tsx">
            {sendState === "sent" ? <p className="flex items-start gap-2.5 text-body text-ink">
                    <WilsonFace variant="bare" className="mt-0.5 h-3.5" />
                    {copy.sent}
                </p> : <form className="flex flex-col gap-3" onSubmit={event => {
      event.preventDefault();
      void send();
    }}>
                    <p className="flex items-start gap-2.5 text-body leading-snug text-ink">
                        <WilsonFace variant="bare" className="mt-1 h-3.5" />
                        {copy.title}
                    </p>
                    <TextArea aria-label={copy.title} placeholder={copy.placeholder} value={note} maxLength={maxLength} ref={noteRef} onChange={event => setNote(event.target.value)} onKeyDown={event => {
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          void send();
        }
      }} />
                    <div className="flex items-center justify-between gap-3">
                        <span className={cn("text-footnote", sendState === "failed" ? "text-tone-red-ink" : "text-ink-3")} aria-live="polite">
                            {sendState === "failed" ? copy.failed : copy.footnote}
                        </span>
                        <Button type="submit" size="sm" hasArrow isBusy={isSending} disabled={!note.trim()}>
                            {copy.send}
                        </Button>
                    </div>
                </form>}
        </Popover>;
}